import {
  CdkConnectedOverlay,
  ConnectedPosition,
  OverlayModule,
  ScrollStrategy,
  createCloseScrollStrategy,
} from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { SEARCH_SCOPES, SearchScope, toSearchScope } from '../search-scope';

let idCounter = 0;

/** Unter der Bereichsauswahl, bei zu wenig Platz darüber — 0,375rem Abstand. */
const PANEL_POSITIONS: ConnectedPosition[] = [
  {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
    offsetY: 6,
  },
  {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
    offsetY: -6,
  },
];

@Component({
  selector: 'app-consultant-search-field',
  standalone: true,
  imports: [OverlayModule, MatIconModule, TranslateModule],
  templateUrl: './consultant-search-field.component.html',
  styleUrl: './consultant-search-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsultantSearchFieldComponent {
  // Ein fehlender Wert darf nie in die Oberflaeche: `undefined` auf `input.value` zeigt
  // der Browser als Text „undefined“, ein fehlender Bereich als `search.scope.undefined`.
  @Input({ required: true })
  set scope(value: SearchScope | null | undefined) {
    this.scopeValue = toSearchScope(value);
  }

  get scope(): SearchScope {
    return this.scopeValue;
  }

  @Input()
  set term(value: string | null | undefined) {
    this.termValue = value ?? '';
  }

  get term(): string {
    return this.termValue;
  }

  private scopeValue: SearchScope = 'name';
  private termValue = '';

  @Output() scopeChange = new EventEmitter<SearchScope>();
  @Output() termInput = new EventEmitter<string>();

  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLElement>;
  @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;
  @ViewChild(CdkConnectedOverlay) private overlay?: CdkConnectedOverlay;

  // Zuweisung im Konstruktor: im gebuendelten Testlauf laedt der Runner die geteilte
  // Konstanten-Datei verzoegert, ein Feld-Initialisierer liest sie dann als undefined.
  protected scopes: readonly SearchScope[] = [];
  protected readonly listboxId = `consultant-search-listbox-${++idCounter}`;
  protected readonly panelPositions = PANEL_POSITIONS;
  protected readonly scrollStrategy: ScrollStrategy;
  protected readonly open = signal(false);
  protected readonly focused = signal(false);
  protected readonly focusedIndex = signal(0);

  constructor(injector: Injector) {
    this.scopes = SEARCH_SCOPES;
    this.scrollStrategy = createCloseScrollStrategy(injector);
  }

  protected onTriggerClick(): void {
    if (this.open()) {
      this.close();
      return;
    }
    this.openList();
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (this.open()) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.openList();
        break;
    }
  }

  protected onListKeydown(event: KeyboardEvent): void {
    const last = this.scopes.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusOption(
          this.focusedIndex() === last ? 0 : this.focusedIndex() + 1,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusOption(
          this.focusedIndex() === 0 ? last : this.focusedIndex() - 1,
        );
        break;
      case 'Home':
        event.preventDefault();
        this.focusOption(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusOption(last);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.select(this.scopes[this.focusedIndex()]);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this.focusTrigger();
        break;
      case 'Tab':
        this.close();
        this.focusTrigger();
        break;
    }
  }

  /** Esc leert das gefuellte Feld; im leeren Feld passiert nichts. */
  protected onInputKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    const input = event.target as HTMLInputElement;
    if (!input.value) return;
    event.preventDefault();
    input.value = '';
    this.termInput.emit('');
  }

  protected onPanelAttached(): void {
    this.focusOption(this.focusedIndex());
  }

  protected onInput(event: Event): void {
    this.termInput.emit((event.target as HTMLInputElement).value);
  }

  protected select(scope: SearchScope): void {
    this.close();
    this.scopeChange.emit(scope);
    this.focusInput();
  }

  protected close(): void {
    this.open.set(false);
  }

  private openList(): void {
    this.focusedIndex.set(Math.max(this.scopes.indexOf(this.scope), 0));
    this.open.set(true);
  }

  /**
   * Echter Fokus statt aria-activedescendant: der Eintrag liegt im Overlay und bekommt den
   * Tastaturfokus, damit Screenreader und Browser ihn gleich behandeln.
   */
  private focusOption(index: number): void {
    this.focusedIndex.set(index);
    this.optionElements()[index]?.focus();
  }

  private optionElements(): HTMLElement[] {
    const panel = this.overlay?.overlayRef?.overlayElement;
    return panel
      ? Array.from(
          panel.querySelectorAll<HTMLElement>('.consultant-search__option'),
        )
      : [];
  }

  private focusTrigger(): void {
    this.triggerRef?.nativeElement.focus();
  }

  private focusInput(): void {
    this.inputRef?.nativeElement.focus();
  }
}
